import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, ChevronLeft, Loader2, Mic, Pause, Play, Plus, Sparkles, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import VoiceCloneCreator from './VoiceCloneCreator';
import CreditBadge from './CreditBadge';
import { fetchClonedVoices, type ClonedVoice } from '../lib/clonedVoicesApi';

// Voz predefinida (tabla `voces`). Misma forma que usa VoiceSelector.
interface PresetVoice {
  id: string;
  nombre: string;
  sexo: string;
  descripcion: string;
  preview_url: string;
  idioma: string;
  acento: string;
}

interface ClipVoicePickerProps {
  open: boolean;
  userId: string | null;
  companyId: string | null;
  /** Costo estimado en créditos de cambiar la voz (según la duración del clip). */
  estimatedCredits: number;
  /** Se dispara al tocar "Generar": elevenlabs voice id + nombre lindo + si es clonada. */
  onGenerate: (voiceId: string, voiceName: string, isCloned: boolean) => void;
  onClose: () => void;
}

const shortNameOf = (n: string) =>
  (n || '').includes(' - ') ? n.split(' - ')[0] : n || 'Sin nombre';

const cleanDescription = (desc: string) => {
  if (!desc) return '';
  if (desc.includes('SILENT THOUGHT') || desc.includes('\\n')) return '';
  if (desc.length > 100) return desc.substring(0, 100) + '…';
  return desc;
};

/**
 * Picker de voz autónomo para la acción "Cambiar voz" de un clip (barra IA).
 * A diferencia del picker embebido en VideoEditor (acoplado al flujo de audio
 * IA por `activePanel`/`sceneMode`), este es un modal/portal independiente:
 * reusa `VoiceCloneCreator` + `fetchClonedVoices` + la tabla `voces`, y emite
 * el voice id elegido por `onSelect`. No toca ningún estado del flujo de audio.
 */
const ClipVoicePicker: React.FC<ClipVoicePickerProps> = ({
  open,
  userId,
  companyId,
  estimatedCredits,
  onGenerate,
  onClose,
}) => {
  // Voz elegida (todavía NO genera): habilita el botón "Generar" de abajo.
  const [selected, setSelected] = useState<
    { voiceId: string; voiceName: string; isCloned: boolean } | null
  >(null);
  const [presets, setPresets] = useState<PresetVoice[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [cloned, setCloned] = useState<ClonedVoice[]>([]);
  const [clonedLoading, setClonedLoading] = useState(false);
  const [tab, setTab] = useState<'atlas' | 'mine'>('atlas');
  const [view, setView] = useState<'list' | 'create'>('list');
  const [sexFilter, setSexFilter] = useState<string>('Todas');
  const [langFilter, setLangFilter] = useState<string>('Todas');
  const [sexMenuOpen, setSexMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [animated, setAnimated] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  // Presets (tabla `voces`) — lazy, una vez al abrir.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setPresetsLoading(true);
      const { data, error } = await supabase
        .from('voces')
        .select('id, nombre, sexo, descripcion, preview_url, idioma, acento')
        .order('nombre');
      if (cancelled) return;
      if (!error && data) setPresets(data as PresetVoice[]);
      setPresetsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Voces clonadas — lazy, una vez al abrir (requiere companyId).
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      setClonedLoading(true);
      const list = await fetchClonedVoices(companyId);
      if (cancelled) return;
      setCloned(list);
      setClonedLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  // Animación de entrada.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
    } else {
      setAnimated(false);
      setView('list');
      setSelected(null);
    }
  }, [open]);

  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  }, []);

  const handleClose = useCallback(() => {
    stopPreview();
    setAnimated(false);
    setTimeout(() => onClose(), 300);
  }, [onClose, stopPreview]);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const togglePreview = useCallback(
    (voice: PresetVoice) => {
      if (playingId === voice.id) {
        stopPreview();
        return;
      }
      stopPreview();
      if (!voice.preview_url) return;
      const audio = new Audio(voice.preview_url);
      audioRef.current = audio;
      setPlayingId(voice.id);
      audio.play().catch(() => setPlayingId(null));
      audio.addEventListener('ended', () => {
        setPlayingId(null);
        audioRef.current = null;
      });
    },
    [playingId, stopPreview],
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    setDragY(0);
    dragStartY.current = null;
  };

  // Elegir una voz la MARCA (habilita "Generar"); no genera todavía.
  const pickPreset = (v: PresetVoice) => {
    setSelected({ voiceId: v.id, voiceName: shortNameOf(v.nombre), isCloned: false });
  };
  const pickCloned = (v: ClonedVoice) => {
    setSelected({ voiceId: v.elevenlabsVoiceId, voiceName: v.nombre, isCloned: true });
  };
  const handleGenerate = () => {
    if (!selected) return;
    stopPreview();
    onGenerate(selected.voiceId, selected.voiceName, selected.isCloned);
  };

  const sexOptions = useMemo(
    () => ['Todas', ...Array.from(new Set(presets.map((v) => v.sexo).filter(Boolean))).sort()],
    [presets],
  );
  const langOptions = useMemo(
    () => ['Todas', ...Array.from(new Set(presets.map((v) => v.idioma).filter(Boolean))).sort()],
    [presets],
  );
  // Filtro AND: una voz aparece sólo si pasa género Y idioma ('Todas' = no filtrar).
  const filteredPresets = useMemo(
    () =>
      presets.filter(
        (v) =>
          (sexFilter === 'Todas' || v.sexo === sexFilter) &&
          (langFilter === 'Todas' || v.idioma === langFilter),
      ),
    [presets, sexFilter, langFilter],
  );

  if (!open) return null;

  const presetCard = (voice: PresetVoice) => {
    const isSelected = selected?.voiceId === voice.id;
    const isPlaying = playingId === voice.id;
    const desc = cleanDescription(voice.descripcion);
    const shortName = shortNameOf(voice.nombre);
    return (
      <button
        key={voice.id}
        type="button"
        onClick={() => pickPreset(voice)}
        className={`w-full text-left rounded-[13px] p-4 transition-all ${
          isSelected
            ? 'bg-[#1644EB]/[0.06] ring-2 ring-[#1644EB]'
            : 'bg-white ring-1 ring-black/[0.06] active:bg-black/[0.02]'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-medium text-brand-black/90 truncate">{shortName}</span>
              {isSelected && <Check className="w-4 h-4 text-[#1644EB] shrink-0" />}
            </div>
            {(voice.idioma || voice.acento) && (
              <p className="text-[13px] text-[#3C3C43]/40 mt-0.5 flex items-center gap-1.5">
                {voice.idioma && <span>{voice.idioma}</span>}
                {voice.idioma && voice.acento && <span className="text-[#3C3C43]/25">·</span>}
                {voice.acento && <span>{voice.acento}</span>}
              </p>
            )}
            {(voice.sexo || desc) && (
              <p className="text-[13px] text-[#3C3C43]/60 mt-1 leading-relaxed">
                {voice.sexo && <span className="font-medium">{voice.sexo}</span>}
                {voice.sexo && desc && <span> · </span>}
                {desc}
              </p>
            )}
          </div>
          {voice.preview_url && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                togglePreview(voice);
              }}
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                isPlaying ? 'bg-[#1644EB] text-white' : 'bg-black/[0.05] text-brand-black/50 hover:bg-black/[0.08]'
              }`}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
          )}
        </div>
      </button>
    );
  };

  const clonedCard = (voice: ClonedVoice) => {
    const isSelected = selected?.voiceId === voice.elevenlabsVoiceId;
    return (
      <button
        key={voice.id}
        type="button"
        onClick={() => pickCloned(voice)}
        className={`w-full text-left rounded-[13px] p-4 transition-all ${
          isSelected
            ? 'bg-[#1644EB]/[0.06] ring-2 ring-[#1644EB]'
            : 'bg-white ring-1 ring-black/[0.06] active:bg-black/[0.02]'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-black/[0.05] flex items-center justify-center shrink-0">
            <Mic className="w-4 h-4 text-brand-black/50" />
          </div>
          <span className="flex-1 text-[15px] font-medium text-brand-black/90 truncate">{voice.nombre}</span>
          {isSelected && <Check className="w-4 h-4 text-[#1644EB] shrink-0" />}
        </div>
      </button>
    );
  };

  const tabsBar = (
    <div className="flex gap-2">
      {(['mine', 'atlas'] as const).map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => setTab(t)}
          className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
            tab === t ? 'bg-[#1644EB] text-white' : 'bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]'
          }`}
        >
          {t === 'atlas' ? 'Voces Atlas' : `Mis voces${cloned.length ? ` (${cloned.length})` : ''}`}
        </button>
      ))}
    </div>
  );

  const body = (() => {
    if (tab === 'atlas') {
      return (
        <>
          <div className="flex gap-2 pb-2">
            {/* Género */}
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => {
                  setSexMenuOpen((o) => !o);
                  setLangMenuOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex flex-col items-start leading-tight min-w-0">
                  <span className="text-[10px] text-brand-black/40 uppercase tracking-wide">Género</span>
                  <span className="text-[13px] font-medium text-brand-black/80 truncate max-w-full">{sexFilter}</span>
                </div>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-neutral-400 shrink-0 transition-transform ${sexMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {sexMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSexMenuOpen(false)} />
                  <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                    {sexOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          setSexFilter(opt);
                          setSexMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors text-[13px] ${
                          sexFilter === opt ? 'text-[#1644EB] font-medium' : 'text-brand-black/80'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Idioma */}
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => {
                  setLangMenuOpen((o) => !o);
                  setSexMenuOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex flex-col items-start leading-tight min-w-0">
                  <span className="text-[10px] text-brand-black/40 uppercase tracking-wide">Idioma</span>
                  <span className="text-[13px] font-medium text-brand-black/80 truncate max-w-full">
                    {langFilter === 'Todas' ? 'Todos' : langFilter}
                  </span>
                </div>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-neutral-400 shrink-0 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {langMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLangMenuOpen(false)} />
                  <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                    {langOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          setLangFilter(opt);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors text-[13px] ${
                          langFilter === opt ? 'text-[#1644EB] font-medium' : 'text-brand-black/80'
                        }`}
                      >
                        {opt === 'Todas' ? 'Todos' : opt}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          {presetsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#1644EB] animate-spin" />
            </div>
          ) : filteredPresets.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-[15px] text-[#3C3C43]/60">No se encontraron voces.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">{filteredPresets.map(presetCard)}</div>
          )}
        </>
      );
    }
    // tab === 'mine'
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          disabled={!userId || !companyId}
          onClick={() => setView('create')}
          className="w-full flex items-center justify-center gap-2 rounded-[13px] py-3 text-[15px] font-medium text-[#1644EB] bg-[#1644EB]/[0.06] ring-1 ring-[#1644EB]/20 hover:bg-[#1644EB]/[0.1] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> Clonar una voz nueva
        </button>
        {clonedLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#1644EB] animate-spin" />
          </div>
        ) : cloned.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-[13px] text-[#3C3C43]/50 text-center">Todavía no clonaste ninguna voz.</p>
          </div>
        ) : (
          cloned.map(clonedCard)
        )}
      </div>
    );
  })();

  // Vista de clonado: monta VoiceCloneCreator; al crear, la agrega y la deja
  // MARCADA (no genera todavía — el usuario toca "Generar").
  const cloneView =
    userId && companyId ? (
      <VoiceCloneCreator
        userId={userId}
        companyId={companyId}
        onBack={() => setView('list')}
        onCreated={(voice) => {
          setCloned((prev) => [voice, ...prev]);
          setTab('mine');
          setView('list');
          setSelected({
            voiceId: voice.elevenlabsVoiceId,
            voiceName: voice.nombre,
            isCloned: true,
          });
        }}
      />
    ) : null;

  // Footer con el botón Generar + el costo estimado (habilitado al elegir voz).
  const footer = (
    <div className="shrink-0 px-3 py-3 border-t border-black/[0.06]">
      <button
        type="button"
        disabled={!selected}
        onClick={handleGenerate}
        className={`w-full py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
          selected
            ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
            : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
        }`}
      >
        <Sparkles className="w-4 h-4" />
        Generar
        {selected && (
          <CreditBadge credits={estimatedCredits} tone="on-primary" size="sm" />
        )}
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop: panel in-flow en la sidebar IZQUIERDA — homogéneo con las
          demás herramientas del editor (380px, border-r, bg-surface-card). El
          slot que lo envuelve en VideoEditor anima el ancho 0↔380. */}
      <div className="hidden sm:flex flex-col min-w-[380px] w-[380px] h-full border-r border-black/[0.06] bg-surface-card overflow-hidden">
        {view === 'create' ? (
          <div className="flex-1 overflow-y-auto">{cloneView}</div>
        ) : (
          <>
            <div className="flex items-center justify-between px-3 h-12 shrink-0">
              <h3 className="text-[15px] font-semibold text-brand-black/90">Cambiar voz del clip</h3>
              <button
                type="button"
                onClick={() => {
                  stopPreview();
                  onClose();
                }}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-3 h-px bg-black/[0.08]" />
            <div className="px-3 pt-3 pb-2">{tabsBar}</div>
            <div className="flex-1 overflow-y-auto px-3 py-3">{body}</div>
            {footer}
          </>
        )}
      </div>

      {/* Mobile: bottom sheet via portal */}
      {ReactDOM.createPortal(
        <>
          <div
            className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${
              animated ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={handleClose}
          />
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
            style={{
              height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform:
                dragY > 0 ? `translateY(${dragY}px)` : animated ? 'translateY(0)' : 'translateY(100%)',
              transition: dragY === 0 ? 'transform 0.3s ease-out' : 'none',
            }}
          >
            {view === 'create' ? (
              <div className="flex-1 overflow-y-auto">
                <div className="flex items-center gap-2 px-4 pt-3">
                  <button
                    type="button"
                    onClick={() => setView('list')}
                    className="text-[15px] text-[#1644EB] active:opacity-50 flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Volver
                  </button>
                </div>
                {cloneView}
              </div>
            ) : (
              <>
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-[15px] text-[#1644EB] active:opacity-50 w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Cambiar voz</h3>
                  <span className="w-20" />
                </div>
                <div className="px-4 pb-2">{tabsBar}</div>
                <div className="flex-1 overflow-y-auto px-4 py-3">{body}</div>
                {footer}
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

export default ClipVoicePicker;
